/**
 * Navigation configuration for Dream TMS
 * Defines all menu items including submenu structure
 */

import {
  LocalShipping as DispatchIcon,
  Inventory as PickupIcon,
  LocalShipping as DeliveryIcon,
  Send as TransferIcon,
  TrackChanges as StatusIcon,
  Announcement as NoticeIcon,
  Settings as SystemIcon,
  Security as PermissionIcon,
  LocationOn as AddressIcon,
  People as UserIcon,
  Notifications as PushIcon,
  PhoneAndroid as AppIcon,
  Map as RegionIcon,
  Code as DevIcon,
  Description as LogsIcon,
  Info as TransmissionDetailIcon,
  CloudDownload as EdocIcon,
  Receipt as SettlementIcon,
  AccessTime as ArrivalTimeIcon,
  Backup as BackupIcon,
} from '@mui/icons-material';
import { MenuItem } from '../types/navigation';

/**
 * Check if current environment is development (local or dev)
 */
const isDevelopmentEnv = (): boolean => {
  const env = import.meta.env.VITE_APP_ENV;
  return env === 'local' || env === 'dev';
};

/**
 * Main navigation configuration
 * 7 main menu items, with 시스템관리 having 5 submenu items
 */
export const navigationConfig: MenuItem[] = [
  {
    label: '배차관리',
    path: '/dispatch',
    icon: <DispatchIcon />,
    description: '배차 작업 관리 및 현황 조회',
    permissionKey: 'dispatch',
  },
  {
    label: '픽업관리',
    path: '/pickup',
    icon: <PickupIcon />,
    description: '픽업 작업 관리 및 현황 조회',
    permissionKey: 'pickup',
  },
  {
    label: '배송관리',
    path: '/delivery',
    icon: <DeliveryIcon />,
    description: '배송 작업 관리 및 현황 조회',
    permissionKey: 'delivery',
  },
  {
    label: '전송관리',
    path: '/transmission',
    icon: <TransferIcon />,
    description: 'EDI 파일 전송 관리',
    permissionKey: 'transfer',
    submenu: [
      {
        label: 'E-POD',
        path: '/transmission/detail',
        icon: <TransmissionDetailIcon />,
        description: 'EDI 전송 상세 정보 조회',
        permissionKey: 'transfer.detail',
      },
      {
        label: 'E-DOC 수·발신 관리',
        path: '/transmission/edoc',
        icon: <EdocIcon />,
        description: 'E-DOC 수·발신 목록 조회',
        permissionKey: 'transfer.edoc',
      },
    ],
  },
  {
    label: '배송현황',
    path: '/status',
    icon: <StatusIcon />,
    description: '실시간 배송 현황 추적',
    permissionKey: 'status',
  },
  // 정산현황 메뉴 — 화면 노출 보류 (요청에 따라 주석 처리)
  // {
  //   label: '정산현황',
  //   path: '/settlement',
  //   icon: <SettlementIcon />,
  //   description: '정산 현황 조회 및 관리',
  //   permissionKey: 'settlement',
  // },
  {
    label: '공지사항',
    path: '/notice',
    icon: <NoticeIcon />,
    description: '시스템 공지사항 및 알림',
    permissionKey: 'notice',
  },
  {
    label: '시스템관리',
    path: '/system',
    icon: <SystemIcon />,
    description: '시스템 설정 및 관리',
    permissionKey: 'system',
    submenu: [
      {
        label: '권한관리',
        path: '/system/permissions',
        icon: <PermissionIcon />,
        description: '사용자 권한 및 역할 관리',
        requiredPermission: 'admin',
        permissionKey: 'system.permission',
      },
      {
        label: '권역관리',
        path: '/system/regions',
        icon: <RegionIcon />,
        description: '지역 권역 정보 관리',
        permissionKey: 'system.region',
      },
      {
        label: '배송지 관리',
        path: '/system/addresses',
        icon: <AddressIcon />,
        description: '배송지 주소 정보 관리',
        permissionKey: 'system.address',
      },
      {
        label: '도착 예정 소요시간 관리',
        path: '/system/arrival-time',
        icon: <ArrivalTimeIcon />,
        description: '권역 간 도착 예정 소요시간 관리',
        permissionKey: 'system.arrivalTime',
      },
      {
        label: '사용자 관리',
        path: '/system/users',
        icon: <UserIcon />,
        description: '시스템 사용자 계정 관리',
        requiredPermission: 'admin',
        permissionKey: 'system.user',
      },
      {
        label: 'PUSH 발송 현황',
        path: '/system/push-notifications',
        icon: <PushIcon />,
        description: '푸시 알림 발송 이력 및 현황',
        permissionKey: 'system.push',
      },
      {
        label: 'APP 버전 및 배포 관리',
        path: '/system/app-versions',
        icon: <AppIcon />,
        description: '모바일 앱 버전 및 배포 관리',
        requiredPermission: 'admin',
        permissionKey: 'system.appVersion',
      },
      {
        label: 'Logs',
        path: '/system/logs',
        icon: <LogsIcon />,
        description: '시스템 로그 조회 및 분석',
        requiredPermission: 'admin',
        permissionKey: 'system.logs',
      },
      {
        label: 'DB백업관리',
        path: '/system/backups',
        icon: <BackupIcon />,
        description: '데이터베이스 백업 및 복구 관리',
        requiredPermission: 'admin',
        permissionKey: 'system.backup',
      },
      // 개발관리 메뉴는 local/dev 환경에서만 표시
      ...(isDevelopmentEnv()
        ? [
            {
              label: '개발관리',
              path: '/system/dev',
              icon: <DevIcon />,
              description: '개발 환경 설정 및 디버깅 도구',
              requiredPermission: 'admin',
              permissionKey: 'system.dev',
            },
          ]
        : []),
    ],
  },
];

/**
 * Find menu item by path
 */
export const findMenuItemByPath = (path: string): MenuItem | undefined => {
  for (const item of navigationConfig) {
    if (item.path === path) {
      return item;
    }
    if (item.submenu) {
      const submenuItem = item.submenu.find(sub => sub.path === path);
      if (submenuItem) {
        return submenuItem;
      }
    }
  }
  return undefined;
};

/**
 * Check if path is under a parent menu item
 */
export const isPathUnderParent = (path: string, parentPath: string): boolean => {
  return path.startsWith(parentPath) && path !== parentPath;
};

/**
 * Get parent menu item for a given path
 */
export const getParentMenuItem = (path: string): MenuItem | undefined => {
  for (const item of navigationConfig) {
    if (item.submenu) {
      const hasSubmenuMatch = item.submenu.some(sub => sub.path === path);
      if (hasSubmenuMatch) {
        return item;
      }
    }
  }
  return undefined;
};
